<mat-toolbar color="primary">
  <mat-toolbar-row class="container">
    <span>{{ 'modals.survey-report-progress.title' | translate }}</span>
    <span class="modal-toolbar-spacer"></span>
    <button mat-icon-button class="ml-auto close-toolbar-btn" type="button" (click)="close()" [matTooltip]="'generic.buttons.close' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </mat-toolbar-row>
</mat-toolbar>

<div class="report-progress">
  @if (!report) {
    <p class="text-muted mb-0">{{ 'modals.survey-report-progress.no-report' | translate }}</p>
  } @else {
    <div class="progress-overview">
      <div>
        <span class="overall-status overall-status-{{ report.status | lowercase }}">
          {{ 'modals.survey-report-progress.status-' + (report.status | lowercase) | translate }}
        </span>
        <div class="overview-meta">
          <mat-icon>schedule</mat-icon>
          {{ 'modals.survey-report-progress.elapsed' | translate: { time: elapsed } }}
          <span class="mx-1">·</span>
          {{ 'modals.survey-report-progress.started-at' | translate: { time: (report.created_at | date: 'HH:mm:ss') } }}
        </div>
      </div>
      <div class="overview-percent">{{ overallPercent }}%</div>
    </div>
    <mat-progress-bar class="mb-3" [mode]="isActive && overallPercent === 0 ? 'indeterminate' : 'determinate'" [value]="overallPercent"></mat-progress-bar>

    @if (report.status === 'CANCELLED') {
      <div class="progress-cancelled mb-3">
        <mat-icon>block</mat-icon>
        <span>{{ 'modals.survey-report-progress.cancelled-hint' | translate }}</span>
      </div>
    }

    @if (report.status === 'FAILED' && report.error_message) {
      <div class="progress-error mb-3">
        <mat-icon>error_outline</mat-icon>
        <span>{{ report.error_message }}</span>
      </div>
    }

    <div class="phases">
      @for (phase of phases; track phase.key; let index = $index) {
        <div class="phase phase-{{ phase.state }}">
          <div class="phase-marker">
            @switch (phase.state) {
              @case ('done') { <mat-icon>check_circle</mat-icon> }
              @case ('failed') { <mat-icon>error</mat-icon> }
              @case ('active') { <mat-spinner diameter="20" strokeWidth="3"></mat-spinner> }
              @case ('skipped') { <mat-icon>remove_circle_outline</mat-icon> }
              @case ('cancelled') { <mat-icon>block</mat-icon> }
              @default { <span class="phase-number">{{ index + 1 }}</span> }
            }
          </div>
          <div class="phase-body">
            <div class="phase-header" [class.clickable]="phase.items.length > 0" (click)="phase.items.length > 0 && toggle(phase.key)">
              <div>
                <div class="phase-title">{{ 'modals.survey-report-progress.phase-' + phase.key | translate }}</div>
                <div class="phase-subtitle">
                  @if (phase.state === 'skipped') {
                    {{ 'modals.survey-report-progress.phase-skipped' | translate }}
                  } @else if (phase.key === 'analyses' || phase.key === 'revisions') {
                    {{ 'modals.survey-report-progress.items-progress' | translate: { done: phase.counts.completed, total: phase.total } }}
                    @if (phase.counts.processing > 0) {
                      · {{ 'modals.survey-report-progress.items-processing' | translate: { count: phase.counts.processing } }}
                    }
                    @if (phase.counts.failed > 0) {
                      · <span class="text-danger">{{ 'modals.survey-report-progress.items-failed' | translate: { count: phase.counts.failed } }}</span>
                    }
                  } @else if (phase.key === 'build') {
                    @if (report.status === 'PENDING') {
                      {{ 'modals.survey-report-progress.build-queued' | translate }}
                    } @else if (phase.total > 0) {
                      {{ 'modals.survey-report-progress.build-progress' | translate: { done: phase.done, total: phase.total } }}
                    } @else {
                      {{ 'modals.survey-report-progress.phase-' + phase.state | translate }}
                    }
                  } @else {
                    @if (phase.state === 'done') {
                      {{ 'modals.survey-report-progress.ready-hint' | translate }}
                    } @else {
                      {{ 'modals.survey-report-progress.phase-' + phase.state | translate }}
                    }
                  }
                </div>
              </div>
              <div class="phase-actions">
                @if (phase.key === 'ready' && phase.state === 'done') {
                  <button mat-raised-button color="primary" type="button" (click)="download(); $event.stopPropagation()">
                    <mat-icon>download</mat-icon>
                    {{ 'modals.survey-report-progress.download' | translate }}
                  </button>
                }
                @if (phase.items.length > 0) {
                  <mat-icon class="phase-toggle">{{ expanded.has(phase.key) ? 'expand_less' : 'expand_more' }}</mat-icon>
                }
              </div>
            </div>
            @if (phase.total > 0 && phase.state !== 'skipped') {
              <mat-progress-bar class="phase-bar" mode="determinate" [value]="phasePercent(phase)"></mat-progress-bar>
            }
            @if (expanded.has(phase.key)) {
              <div class="phase-items">
                @for (item of phase.items; track item.id) {
                  <div class="phase-item">
                    <span class="item-label" [matTooltip]="item.label">{{ item.label }}</span>
                    <span class="item-chips">
                      <span class="item-chip item-action">{{ 'modals.survey-report-progress.action-' + item.action | translate }}</span>
                      <span class="item-chip item-state-{{ item.state }}">{{ 'modals.survey-report-progress.state-' + item.state | translate }}</span>
                    </span>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>

    @if (report.status === 'COMPLETED' && report.issues?.items?.length) {
      <h6 class="section-label mt-4">{{ 'modals.survey-report-progress.issues' | translate: { count: report.issues.items.length } }}</h6>
      <div class="issues-list">
        @for (issue of report.issues.items; track $index) {
          <div class="phase-item">
            <span class="item-label" [matTooltip]="issue.message || ''">{{ issue.question }}</span>
            <span class="item-chip item-state-failed">{{ 'modals.survey-report-progress.issue-' + issue.type | translate }}</span>
          </div>
        }
      </div>
    }

    <h6 class="section-label mt-4">{{ 'modals.survey-report-progress.timeline' | translate }}</h6>
    @if (events.length === 0) {
      <p class="small text-muted mb-0">{{ 'modals.survey-report-progress.timeline-empty' | translate }}</p>
    } @else {
      <div class="timeline">
        @for (event of events; track $index) {
          <div class="timeline-row">
            <span class="timeline-time">{{ event.at | date: 'HH:mm:ss' }}</span>
            <span class="timeline-dot timeline-dot-{{ event.type }}"></span>
            <span class="timeline-text">
              {{ 'modals.survey-report-progress.event-' + event.type | translate: event.params }}
              @if (event.params?.label) {
                <span class="text-muted"> — {{ event.params.label }}</span>
              }
            </span>
          </div>
        }
      </div>
    }
  }
</div>

@if (confirmingCancel && isActive) {
  <div class="cancel-confirm container mb-3">
    <mat-icon>warning_amber</mat-icon>
    <div class="cancel-confirm-text">
      <strong>{{ 'modals.survey-report-progress.cancel-confirm-title' | translate }}</strong>
      <span>{{ 'modals.survey-report-progress.cancel-confirm-text' | translate }}</span>
    </div>
    <div class="cancel-confirm-actions">
      <button mat-stroked-button type="button" [disabled]="cancelling" (click)="keepGenerating()">
        {{ 'modals.survey-report-progress.cancel-keep' | translate }}
      </button>
      <button mat-flat-button class="cancel-confirm-button" type="button" [disabled]="cancelling" (click)="cancelGeneration()">
        @if (cancelling) {
          <mat-spinner diameter="18" strokeWidth="3"></mat-spinner>
        } @else {
          {{ 'modals.survey-report-progress.cancel-yes' | translate }}
        }
      </button>
    </div>
  </div>
}

<div class="buttons-container container mb-3 d-flex align-items-center">
  @if (isActive && !confirmingCancel) {
    <button mat-button class="cancel-generation-button" type="button" (click)="askCancel()">
      <mat-icon>stop_circle</mat-icon>
      {{ 'modals.survey-report-progress.cancel' | translate }}
    </button>
  }
  <button mat-stroked-button class="text-dark-purple border-purple ml-auto" type="button" (click)="close()">
    {{ (isActive ? 'modals.survey-report-progress.continue-background' : 'generic.buttons.close') | translate }}
  </button>
</div>
